import type {
  ActiveMenuItemPromotion,
  MenuItem,
  MenuItemPromotionRule,
  MenuItemVariant,
  MenuModifier,
  DietaryTag,
  PrepStation,
  TaxClass,
} from '@/lib/types/pos'

const PREP_STATIONS: PrepStation[] = ['kitchen', 'bar', 'grill', 'cold', 'none']
const TAX_CLASSES: TaxClass[] = ['standard', 'reduced', 'zero']

function isPrepStation(value: unknown): value is PrepStation {
  return typeof value === 'string' && PREP_STATIONS.includes(value as PrepStation)
}

function isTaxClass(value: unknown): value is TaxClass {
  return typeof value === 'string' && TAX_CLASSES.includes(value as TaxClass)
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : []
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : []
}

function toMoneyNumber(value: unknown): number {
  const numberValue = Number(value ?? 0)
  return Number.isFinite(numberValue) ? Math.round(numberValue * 100) / 100 : 0
}

function normalizePromotionRules(value: unknown): MenuItemPromotionRule[] {
  if (!Array.isArray(value)) return []
  return value
    .map((rule: any) => {
      const days = Array.isArray(rule?.days)
        ? rule.days
            .map((day: unknown) => Number(day))
            .filter((day: number) => Number.isInteger(day) && day >= 1 && day <= 7)
        : []
      const price = toMoneyNumber(rule?.price)
      if (price <= 0) return null
      return {
        days,
        price,
        startsAt: typeof rule?.startsAt === 'string' && rule.startsAt.trim() ? rule.startsAt.trim() : null,
        endsAt: typeof rule?.endsAt === 'string' && rule.endsAt.trim() ? rule.endsAt.trim() : null,
        carryOverUntil:
          typeof rule?.carryOverUntil === 'string' || typeof rule?.carryOverUntil === 'number'
            ? rule.carryOverUntil
            : null,
        label: typeof rule?.label === 'string' && rule.label.trim() ? rule.label.trim() : null,
      }
    })
    .filter(Boolean) as MenuItemPromotionRule[]
}

function previousPromotionDay(day: number) {
  return day === 1 ? 7 : day - 1
}

function getPromotionMoment(now: Date, timezoneId?: string | null) {
  if (timezoneId) {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezoneId,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(now)
      const part = (type: string) => parts.find((entry) => entry.type === type)?.value
      const dayMap: Record<string, number> = {
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
        Sun: 7,
      }
      const day = dayMap[part('weekday') ?? '']
      const hour = Number(part('hour'))
      const minute = Number(part('minute'))
      if (day && Number.isFinite(hour) && Number.isFinite(minute)) {
        return { day, minutes: hour * 60 + minute }
      }
    } catch {
      // Fall back to the device timezone if the restaurant timezone is invalid.
    }
  }

  const jsDay = now.getDay()
  return {
    day: jsDay === 0 ? 7 : jsDay,
    minutes: now.getHours() * 60 + now.getMinutes(),
  }
}

function parsePromotionTimeToMinutes(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 0 && value <= 1440 ? Math.floor(value) : null
  }
  if (typeof value !== 'string') return null
  const match = value.trim().match(/^(\d{1,2})(?::?(\d{2}))?$/)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2] ?? 0)
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) {
    return null
  }
  if (hours === 24 && minutes !== 0) return null
  return hours * 60 + minutes
}

function normalizeActivePromotion(value: unknown): ActiveMenuItemPromotion | null {
  const promotion = (value ?? {}) as Partial<ActiveMenuItemPromotion>
  const price = toMoneyNumber(promotion.price)
  const regularPrice = toMoneyNumber(promotion.regularPrice)
  if (price <= 0 || regularPrice <= 0) return null
  return {
    price,
    regularPrice,
    label: promotion.label ?? null,
  }
}

export function getActiveMenuItemPromotion(
  regularBasePrice: number,
  rules: MenuItemPromotionRule[] | null | undefined,
  now = new Date(),
  timezoneId?: string | null,
): ActiveMenuItemPromotion | null {
  if (!Array.isArray(rules) || rules.length === 0) return null
  const { day, minutes } = getPromotionMoment(now, timezoneId)
  const timestamp = now.getTime()

  for (const rule of rules) {
    const days = Array.isArray(rule.days) ? rule.days.map(Number).filter(Number.isFinite) : []
    if (days.length > 0 && !days.includes(day)) {
      const carryOverUntil = parsePromotionTimeToMinutes(rule.carryOverUntil)
      if (carryOverUntil == null || minutes >= carryOverUntil || !days.includes(previousPromotionDay(day))) continue
    }

    const startsAt = rule.startsAt ? new Date(rule.startsAt).getTime() : null
    const endsAt = rule.endsAt ? new Date(rule.endsAt).getTime() : null
    if (startsAt && Number.isFinite(startsAt) && timestamp < startsAt) continue
    if (endsAt && Number.isFinite(endsAt) && timestamp > endsAt) continue

    const price = toMoneyNumber(rule.price)
    if (price <= 0) continue
    return {
      price,
      regularPrice: regularBasePrice,
      label: rule.label ?? null,
    }
  }

  return null
}

export function normalizeMenuItemVariant(value: unknown): MenuItemVariant {
  const variant = (value ?? {}) as Partial<MenuItemVariant>
  return {
    id: variant.id ?? 0,
    documentId: variant.documentId ?? '',
    bookosId: variant.bookosId ?? '',
    name: variant.name ?? '',
    priceDelta: Number(variant.priceDelta ?? 0),
    isAvailable: variant.isAvailable ?? true,
  }
}

export function normalizeMenuModifier(value: unknown): MenuModifier {
  const modifier = (value ?? {}) as Partial<MenuModifier>
  return {
    id: modifier.id ?? 0,
    documentId: modifier.documentId ?? '',
    bookosId: modifier.bookosId ?? '',
    name: modifier.name ?? '',
    priceDelta: Number(modifier.priceDelta ?? 0),
  }
}

export function normalizeDietaryTag(value: unknown): DietaryTag {
  const tag = (value ?? {}) as Partial<DietaryTag>
  return {
    id: tag.id ?? 0,
    documentId: tag.documentId ?? '',
    bookosId: tag.bookosId ?? '',
    name: tag.name ?? '',
    icon: tag.icon ?? 'AlertTriangle',
    color: tag.color ?? '#B7791F',
    sortOrder: Number(tag.sortOrder ?? 0),
  }
}

export function normalizeMenuItem(value: unknown): MenuItem {
  const item = (value ?? {}) as Partial<MenuItem>
  const regularBasePrice = toMoneyNumber(item.regularBasePrice ?? item.basePrice)
  const promotionRules = normalizePromotionRules(item.promotionRules)
  const activePromotion = promotionRules.length > 0
    ? getActiveMenuItemPromotion(regularBasePrice, promotionRules, new Date(), item.restaurant?.timezoneId)
    : normalizeActivePromotion(item.activePromotion)

  return {
    id: item.id ?? 0,
    documentId: item.documentId ?? '',
    bookosId: item.bookosId ?? '',
    name: item.name ?? '',
    description: item.description ?? '',
    basePrice: activePromotion?.price ?? toMoneyNumber(item.basePrice),
    soldByWeight: item.soldByWeight === true,
    estimatedWeightGramsPerPiece: item.estimatedWeightGramsPerPiece == null
      ? null
      : Math.max(1, Math.round(Number(item.estimatedWeightGramsPerPiece) || 0)),
    regularBasePrice,
    promotionRules,
    activePromotion,
    restaurant: item.restaurant ?? null,
    taxClass: isTaxClass(item.taxClass) ? item.taxClass : 'standard',
    isAvailable: item.isAvailable ?? true,
    stockTracking: item.stockTracking ?? false,
    stockCount: item.stockCount ?? null,
    prepStation: isPrepStation(item.prepStation) ? item.prepStation : 'none',
    imageUrl: item.imageUrl ?? null,
    sortOrder: Number(item.sortOrder ?? 0),
    orderRole: item.orderRole === 'child_only' ? 'child_only' : 'standalone',
    extraAppliesToCategories: asStringArray(item.extraAppliesToCategories),
    extraAppliesToItems: asStringArray(item.extraAppliesToItems),
    category: item.category ?? null,
    variants: asArray<MenuItemVariant>(item.variants).map(normalizeMenuItemVariant),
    modifiers: asArray<MenuModifier>(item.modifiers).map(normalizeMenuModifier),
    dietaryTags: asArray<DietaryTag>(item.dietaryTags).map(normalizeDietaryTag),
  }
}

export function normalizeMenuItems(values: unknown): MenuItem[] {
  return asArray<MenuItem>(values).map(normalizeMenuItem)
}
